/**
 * React Hook for Database Translation
 * 
 * This hook provides a simple interface for translating database records
 * with field-level control over what gets translated.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useDatabaseTranslation, TranslationScope, TranslatableRecord } from '@/utils/databaseTranslation'

interface UseDatabaseTranslationOptions {
    sourceLanguage?: string
    autoTranslate?: boolean
}

interface UseDatabaseTranslationResult<T> {
    data: T
    isLoading: boolean
    isTranslated: boolean
    translate: () => Promise<void>
    reset: () => void
}

/**
 * Hook for translating a single database record
 */
export function useDatabaseTranslationRecord<T extends TranslatableRecord>(
    record: T,
    translationScope: TranslationScope,
    options: UseDatabaseTranslationOptions = {}
): UseDatabaseTranslationResult<T> {
    const { translateRecord, isTranslationEnabled } = useDatabaseTranslation()
    const [data, setData] = useState<T>(record)
    const [isLoading, setIsLoading] = useState(false)
    const [isTranslated, setIsTranslated] = useState(false)

    const { sourceLanguage, autoTranslate = true } = options

    const performTranslation = useCallback(async () => {
        if (!isTranslationEnabled || !record) {
            setData(record)
            setIsTranslated(false)
            return
        }

        setIsLoading(true)
        try {
            const translatedRecord = await translateRecord(record, translationScope, sourceLanguage)
            setData(translatedRecord)
            setIsTranslated(true)
        } catch (error) {
            console.error('Database translation error:', error)
            setData(record)
            setIsTranslated(false)
        } finally {
            setIsLoading(false)
        }
    }, [record, translationScope, sourceLanguage, isTranslationEnabled, translateRecord])

    // Auto-translate when record changes and auto-translate is enabled
    useEffect(() => {
        if (autoTranslate && isTranslationEnabled) {
            performTranslation()
        } else {
            setData(record)
            setIsTranslated(false)
        }
    }, [record, isTranslationEnabled, autoTranslate, sourceLanguage, performTranslation])

    const translate = useCallback(async () => {
        await performTranslation()
    }, [performTranslation])

    const reset = useCallback(() => {
        setData(record)
        setIsTranslated(false)
    }, [record])

    return {
        data,
        isLoading,
        isTranslated,
        translate,
        reset,
    }
}

/**
 * Hook for translating an array of database records
 */
export function useDatabaseTranslationRecords<T extends TranslatableRecord>(
    records: T[],
    translationScope: TranslationScope,
    options: UseDatabaseTranslationOptions = {}
): UseDatabaseTranslationResult<T[]> {
    const { translateRecords, isTranslationEnabled } = useDatabaseTranslation()
    const [data, setData] = useState<T[]>(records)
    const [isLoading, setIsLoading] = useState(false)
    const [isTranslated, setIsTranslated] = useState(false)
    const { sourceLanguage, autoTranslate = true } = options
    
    // Use refs to track previous values and prevent unnecessary re-renders
    const prevRecordsRef = useRef<T[]>([])
    const prevEnabledRef = useRef<boolean>(false)
    const isInitialMount = useRef(true)

    // Auto-translate when records change and auto-translate is enabled
    useEffect(() => {
        // Handle initial mount
        if (isInitialMount.current) {
            isInitialMount.current = false
            prevRecordsRef.current = records
            prevEnabledRef.current = isTranslationEnabled
            
            // On initial mount, translate if enabled, otherwise just set data
            if (!autoTranslate || !isTranslationEnabled || !records || records.length === 0) {
                setData(records)
                setIsTranslated(false)
                return
            }
            
            // Perform initial translation
            setIsLoading(true)
            translateRecords(records, translationScope, sourceLanguage)
                .then((translatedRecords) => {
                    setData(translatedRecords)
                    setIsTranslated(true)
                })
                .catch((error) => {
                    console.error('Database translation error:', error)
                    setData(records)
                    setIsTranslated(false)
                })
                .finally(() => {
                    setIsLoading(false)
                })
            return
        }

        // Check if records actually changed (shallow comparison by length and IDs if available)
        const recordsChanged = 
            prevRecordsRef.current.length !== records.length ||
            prevRecordsRef.current.some((prev, idx) => {
                const curr = records[idx]
                return !curr || (prev.id !== undefined && curr.id !== undefined && prev.id !== curr.id)
            })
        const enabledChanged = prevEnabledRef.current !== isTranslationEnabled

        // Skip if nothing actually changed
        if (!recordsChanged && !enabledChanged) {
            return
        }

        // Update refs
        prevRecordsRef.current = records
        prevEnabledRef.current = isTranslationEnabled

        if (!autoTranslate || !isTranslationEnabled) {
            setData(records)
            setIsTranslated(false)
            return
        }

        if (!records || records.length === 0) {
            setData(records)
            setIsTranslated(false)
            return
        }

        // Perform translation (console logs removed for performance)
        setIsLoading(true)
        translateRecords(records, translationScope, sourceLanguage)
            .then((translatedRecords) => {
                setData(translatedRecords)
                setIsTranslated(true)
            })
            .catch((error) => {
                console.error('❌ Database translation error:', error)
                setData(records)
                setIsTranslated(false)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [records, isTranslationEnabled, autoTranslate, sourceLanguage, translationScope, translateRecords])

    const performTranslation = useCallback(async () => {
        if (!isTranslationEnabled || !records || records.length === 0) {
            setData(records)
            setIsTranslated(false)
            return
        }

        setIsLoading(true)
        try {
            const translatedRecords = await translateRecords(records, translationScope, sourceLanguage)
            setData(translatedRecords)
            setIsTranslated(true)
        } catch (error) {
            console.error('Database translation error:', error)
            setData(records)
            setIsTranslated(false)
        } finally {
            setIsLoading(false)
        }
    }, [records, translationScope, sourceLanguage, isTranslationEnabled, translateRecords])

    const translate = useCallback(async () => {
        await performTranslation()
    }, [performTranslation])

    const reset = useCallback(() => {
        setData(records)
        setIsTranslated(false)
    }, [records])

    return {
        data,
        isLoading,
        isTranslated,
        translate,
        reset,
    }
}

/**
 * Hook for translating nested database records (e.g., users with roles)
 */
export function useDatabaseTranslationNested<T extends TranslatableRecord>(
    record: T,
    translationScope: TranslationScope,
    nestedScopes?: { [nestedKey: string]: TranslationScope },
    options: UseDatabaseTranslationOptions = {}
): UseDatabaseTranslationResult<T> {
    const { translateNestedRecord, isTranslationEnabled } = useDatabaseTranslation()
    const [data, setData] = useState<T>(record)
    const [isLoading, setIsLoading] = useState(false)
    const [isTranslated, setIsTranslated] = useState(false)

    const { sourceLanguage, autoTranslate = true } = options

    const performTranslation = useCallback(async () => {
        if (!isTranslationEnabled || !record) {
            setData(record)
            setIsTranslated(false)
            return
        }

        setIsLoading(true)
        try {
            const translatedRecord = await translateNestedRecord(
                record,
                translationScope,
                nestedScopes,
                sourceLanguage
            )
            setData(translatedRecord)
            setIsTranslated(true)
        } catch (error) {
            console.error('Database translation error:', error)
            setData(record)
            setIsTranslated(false)
        } finally {
            setIsLoading(false)
        }
    }, [record, translationScope, nestedScopes, sourceLanguage, isTranslationEnabled, translateNestedRecord])

    // Auto-translate when record changes and auto-translate is enabled
    useEffect(() => {
        if (autoTranslate && isTranslationEnabled) {
            performTranslation()
        } else {
            setData(record)
            setIsTranslated(false)
        }
    }, [record, isTranslationEnabled, autoTranslate, sourceLanguage, performTranslation])

    const translate = useCallback(async () => {
        await performTranslation()
    }, [performTranslation])

    const reset = useCallback(() => {
        setData(record)
        setIsTranslated(false)
    }, [record])

    return {
        data,
        isLoading,
        isTranslated,
        translate,
        reset,
    }
}

